import type {
  GithubInstallStatus,
  GithubInstallStatusInstalled,
  GithubInstallStatusPending,
} from '@/common/api/types/effortSpend';
import type { Loop } from '@/common/api/types/loops';
import {
  ExternalWorkIntegrationStatusValues,
  type JiraInstance,
  type WorkIntegrationInstance,
  type WorkIntegrationResponse,
} from '@/common/api/types/workIntegrations';

export const isCompletedWorkIntegrationStatus = (
  status: ExternalWorkIntegrationStatusValues | undefined,
): boolean =>
  status === ExternalWorkIntegrationStatusValues.COMPLETED ||
  status === ExternalWorkIntegrationStatusValues.COMPLETED_WITH_PENDING;

export const isTerminalWorkIntegrationStatus = (
  status: ExternalWorkIntegrationStatusValues,
): boolean =>
  isCompletedWorkIntegrationStatus(status) ||
  status === ExternalWorkIntegrationStatusValues.FAILED ||
  status === ExternalWorkIntegrationStatusValues.PARTIAL_FAILURE;

/** Mirrors backend `IntegrationStatus.allowsEdit()`; excludes COMPLETED_WITH_PENDING (edits 409 while items are still pending). */
export const isEditableWorkIntegrationStatus = (
  status: ExternalWorkIntegrationStatusValues | undefined,
): boolean =>
  status === ExternalWorkIntegrationStatusValues.COMPLETED ||
  status === ExternalWorkIntegrationStatusValues.FAILED ||
  status === ExternalWorkIntegrationStatusValues.PARTIAL_FAILURE;

// Excludes CREATED: a never-synced integration is idle, not busy — see isWorkIntegrationImportInProgress.
export const isWorkIntegrationSyncing = (
  status: ExternalWorkIntegrationStatusValues | undefined,
): boolean =>
  status === ExternalWorkIntegrationStatusValues.RUNNING ||
  status === ExternalWorkIntegrationStatusValues.QUEUED;

export const isWorkIntegrationImportInProgress = (
  status: ExternalWorkIntegrationStatusValues | undefined,
): boolean =>
  status === ExternalWorkIntegrationStatusValues.CREATED || isWorkIntegrationSyncing(status);

interface SyncTimestamps {
  lastPullSyncAt?: string | null;
  lastPushSyncAt?: string | null;
}

//  Which direction the sync that just completed ran in.
export const completedSyncWasPush = (
  entity: SyncTimestamps,
  previous: SyncTimestamps | undefined,
): boolean => {
  if (!entity.lastPushSyncAt) return false;
  if (previous) return entity.lastPushSyncAt !== previous.lastPushSyncAt;
  return (
    !!entity.lastPullSyncAt && new Date(entity.lastPushSyncAt) > new Date(entity.lastPullSyncAt)
  );
};

export const parseHostname = (url: string): string => {
  try {
    const urlObj = new URL(url);
    const hostname = urlObj.hostname;
    const parts = hostname.split('.');
    return parts[0] || hostname;
  } catch {
    return url;
  }
};

export const filterOnlyRegisteredInstances = (
  instances: JiraInstance[],
  registeredInstances: WorkIntegrationInstance[] | undefined,
): JiraInstance[] => {
  if (!registeredInstances) return instances;
  return instances.filter((instance) =>
    registeredInstances.some((reg) => reg.instanceId === instance.id),
  );
};

export const buildLoopOptions = (loopsById: Record<number, Loop>, allLoopsLabel: string) => {
  const allLoops = { value: null, label: allLoopsLabel };
  const options = Object.values(loopsById)
    .map((loop) => ({ value: loop.id, label: loop.name }))
    .sort((a, b) => a.label.localeCompare(b.label));
  return [allLoops, ...options];
};

export const filterWorkIntegrations = ({
  workIntegrations,
  search,
}: {
  workIntegrations: WorkIntegrationResponse[];
  search: string;
}) => {
  if (!search) {
    return workIntegrations;
  }

  return workIntegrations.filter((integration) =>
    integration.name?.toLowerCase().includes(search.toLowerCase()),
  );
};

export const parseLoopId = (value: unknown): number | undefined => {
  const n = Number(value);
  return Number.isFinite(n) && Number.isInteger(n) && n > 0 ? n : undefined;
};

export const getLoopIdFromReturnUrl = (returnUrl: string): number | undefined => {
  const url = new URL(returnUrl, window.location.origin);
  return parseLoopId(url.searchParams.get('loopId'));
};

export const filterUnregisteredInstances = (
  instances: JiraInstance[],
  registeredInstances: WorkIntegrationInstance[] | undefined,
): JiraInstance[] => {
  if (!registeredInstances) return instances;
  return instances.filter(
    (instance) => !registeredInstances.some((reg) => reg.instanceId === instance.id),
  );
};

export const instanceMatchesSearch = (
  instance: WorkIntegrationInstance,
  children: WorkIntegrationResponse[],
  search: string,
): boolean => {
  if (!search) {
    return true;
  }
  const term = search.toLowerCase();
  const haystack = [
    parseHostname(instance.instanceUrl),
    instance.instanceName ?? '',
    instance.instanceUrl,
  ];
  if (haystack.some((value) => value.toLowerCase().includes(term))) {
    return true;
  }
  return children.some((child) => child.name?.toLowerCase().includes(term));
};

export const isGithubInstalled = (
  status: GithubInstallStatus | undefined,
): status is GithubInstallStatusInstalled => status?.state === 'installed';

export const isGithubPending = (
  status: GithubInstallStatus | undefined,
): status is GithubInstallStatusPending => status?.state === 'pending';

export const githubMatchesSearch = (
  status: GithubInstallStatus | undefined,
  search: string,
): boolean => {
  if (!search) {
    return true;
  }
  const term = search.toLowerCase();
  if ('github'.startsWith(term)) {
    return true;
  }
  if (!isGithubInstalled(status)) {
    return false;
  }
  const haystack = [status.githubOrgName, status.githubInstallationId, ...status.repos];
  return haystack.some((value) => value.toLowerCase().includes(term));
};
